import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";
import { ElMessage } from "element-plus";

type TypedRequestConfig = AxiosRequestConfig;

type DataReturningAxios = Omit<
  AxiosInstance,
  "get" | "delete" | "post" | "put" | "patch"
> & {
  get<T = any, R = T>(url: string, config?: TypedRequestConfig): Promise<R>;
  delete<T = any, R = T>(
    url: string,
    config?: TypedRequestConfig,
  ): Promise<R>;
  post<T = any, R = T>(
    url: string,
    data?: any,
    config?: TypedRequestConfig,
  ): Promise<R>;
  put<T = any, R = T>(
    url: string,
    data?: any,
    config?: TypedRequestConfig,
  ): Promise<R>;
  patch<T = any, R = T>(
    url: string,
    data?: any,
    config?: TypedRequestConfig,
  ): Promise<R>;
};

// 创建 axios 实例
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "http://localhost:5000/api",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
}) as DataReturningAxios;

// 请求拦截器
api.interceptors.request.use(
  async (config) => {
    const { useUserStore } = await import("@/stores/user");
    const userStore = useUserStore();

    // 添加认证令牌
    if (userStore.tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${userStore.tokens.accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    const { data } = response;

    // API 成功响应格式: { success: true, data, message, code }
    if (data && typeof data === "object" && "success" in data) {
      if (data.success) {
        return data.data;
      }
      throw new Error(data.message || "请求失败");
    }

    return data;
  },
  async (error) => {
    if (error.response) {
      const { status, data } = error.response;

      // 处理401错误 - 静默处理，不自动跳转
      if (status === 401) {
        throw new Error("未登录或登录已过期");
      }

      // 处理其他错误
      const message = data?.message || getErrorMessage(status);
      ElMessage.error(message);
      throw new Error(message);
    }

    // 网络错误
    if (error.code === "NETWORK_ERROR") {
      ElMessage.error("网络连接失败，请检查网络设置");
      throw error;
    }

    // 超时错误
    if (error.code === "ECONNABORTED") {
      ElMessage.error("请求超时，请稍后重试");
      throw error;
    }

    throw error;
  },
);

function getErrorMessage(status: number): string {
  const messages: Record<number, string> = {
    400: "请求参数错误",
    401: "未授权，请登录",
    403: "拒绝访问",
    404: "请求地址出错",
    408: "请求超时",
    500: "服务器内部错误",
    501: "服务未实现",
    502: "网关错误",
    503: "服务不可用",
    504: "网关超时",
    505: "HTTP版本不受支持",
  };
  return messages[status] || `连接出错(${status})`;
}

export default api;
